import { Alert, Button, PageHeader } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { ValidationErrorCode } from '@common/enums/validationErrorCode';
import type { Uuid } from '@common/utils/uuid';
import type { IServiceGetOne } from '@frontend/components/interfaces';
import type { FundingServicePart, NewFundingServicePart } from '@frontend/services/api/fundingServicePartsApiService';
import { ManagerAuthorization } from '@common/enums/managerAuthorization';
import { getLanguage } from '@common/translate/translate';
import {
    createFundingServicePart,
    getFundingServiceParts,
    removeFundingServicePart,
    updateFundingServicePart,
} from '@frontend/services/api/fundingServicePartsApiService';
import { getServiceActivities } from '@frontend/services/api/servicesApiService';
import { hasOneAuthorization } from '@frontend/utils/hasAuthorization';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import { FundingServicePartAddEdit } from './FundingServicePartAddEdit';
import { FundingServicePartsList } from './FundingServicePartsList';

type FundingServicePartsProps = {
    service: IServiceGetOne;
};

export type FundableServiceActivity = {
    code: string;
    id: Uuid;
    name: string;
};

export function FundingServiceParts(props: FundingServicePartsProps) {
    const translate = getLanguage();
    const abortSignal = useAbortSignal();

    const canEdit = hasOneAuthorization([
        ManagerAuthorization.financeFunding,
        ManagerAuthorization.serviceDeclarationRules,
    ]);

    const [serviceActivities, setServiceActivities] = useState<FundableServiceActivity[]>([]);
    const [fundingServiceParts, setFundingServiceParts] = useState<FundingServicePart[]>([]);
    const [editingServicePart, setEditingServicePart] = useState<NewFundingServicePart | FundingServicePart | null>(
        null
    );
    const [removeError, setRemoveError] = useState<ValidationErrorCode.CANNOT_DELETE_RESOURCE_IN_USE | undefined>();

    useEffect(() => {
        load();
    }, [props.service.id]);

    return (
        <>
            <PageHeader
                action={
                    canEdit &&
                    !editingServicePart && (
                        <Button
                            iconLeft="add"
                            onClick={onAdd}
                        >
                            {translate.common.add}
                        </Button>
                    )
                }
                title={translate.finance.fundingServicePart.shortName + ' ' + props.service.name}
            />
            {removeError && !editingServicePart && (
                <Alert appearance="warning">{translate.finance.fundingServicePart.removeError}</Alert>
            )}
            {!editingServicePart && (
                <FundingServicePartsList
                    canEdit={canEdit}
                    onEdit={onEdit}
                    onRemove={onRemove}
                    parts={fundingServiceParts}
                    serviceActivities={serviceActivities}
                />
            )}

            {editingServicePart && (
                <FundingServicePartAddEdit
                    cancel={onCancel}
                    part={editingServicePart}
                    save={onSave}
                    serviceActivities={serviceActivities}
                />
            )}
        </>
    );

    function onAdd() {
        setEditingServicePart({
            fundingForActivityCodes: [],
            invoiceServiceCode: null,
            invoiceServiceName: null,
            invoiceServiceType: null,
            name: '',
        });
    }

    async function onCancel() {
        setEditingServicePart(null);
        await load();
    }

    function onEdit(id: string) {
        setEditingServicePart(fundingServiceParts.find((part) => part.id === id) ?? null);
    }

    async function onRemove(id: Uuid) {
        const error = await removeFundingServicePart(id);
        await load();
        if (error) {
            setRemoveError(error);
        }
    }

    async function onSave(part: FundingServicePart | NewFundingServicePart) {
        if (Object.hasOwn(part, 'id')) {
            await updateFundingServicePart(props.service.id as Uuid, part as FundingServicePart);
        } else {
            await createFundingServicePart(props.service.id as Uuid, part);
        }
        setEditingServicePart(null);
        await load();
    }

    async function load() {
        setRemoveError(undefined);
        const activities = await getServiceActivities(props.service.id, abortSignal);
        const mappedActivities = activities.map((activity) => ({
            code: `${props.service.code}_${activity.customControlCode}`,
            id: activity.id as Uuid,
            name: activity.name,
        }));

        setServiceActivities(mappedActivities);

        const parts = await getFundingServiceParts(props.service.id as Uuid, abortSignal);
        setFundingServiceParts(parts);
    }
}
